/**
 * Whether browser Back stays inside the app: the seam behind every Back that
 * pops (the dock's Backs, the trail's Back, the peek's close, the error
 * screens' Back).
 *
 * Two ways a blind `router.back()` walks out of the app entirely: the Next
 * router can *replace* instead of push when a link is clicked while another
 * transition is in flight (docs/archive/TECH_DEBT_RESOLVED.md, "Modal close
 * can walk out of the app"), and a shared or direct link boots the app with no
 * in-app entry below. The browser exposes no "where does Back go" API, so the
 * app keeps its own answer: a POSITION, stamped into each history entry it
 * writes as `__tIdx`. The entry the app booted on is 0, each push is one more
 * than the entry it was pushed from, and a replace keeps the stamp of the
 * entry it replaces (the Next router replaces the current entry on every
 * commit, which must never move the position). Back and Forward restore the
 * position from the entry's stamp (`popstate`), and a reload keeps it, since
 * the entries keep their state. Back stays in the app when the position is
 * above 0.
 *
 * Why not `history.length` against its value at boot (the rule until
 * 2026-10-20): the length never shrinks on Back, so after popping back to the
 * entry the app started on, the check still said "in-app", and Back popped out
 * of the app in a browser tab, or did nothing in an app installed to an iOS
 * home screen, while the dock drew it lit.
 *
 * When popping would leave the app, a Back walks to its fallback with
 * `router.replace`, never `router.push`: a pushed fallback adds an in-app
 * entry, so the next Back pops to the page just left and the two pages bounce
 * between each other. A replaced fallback walks up and leaves no entry behind.
 *
 * Known softness, accepted: a Next navigation that replaces where it should
 * push (the race above) is counted as no step, so Back walks up to the
 * fallback instead of popping: still in the app, never out of it. A page the
 * browser loads in full inside the app starts at 0 again for the same result.
 */

/** The key of the position in a history entry's state. */
export const POSITION_KEY = '__tIdx';

/** The position a history entry's state carries, or null when it has none. */
export function positionOf(state: unknown): number | null {
  if (typeof state !== 'object' || state === null) return null;
  const value = (state as Record<string, unknown>)[POSITION_KEY];
  return typeof value === 'number' ? value : null;
}

/**
 * `state` with `position` stamped into it: a copy, so the caller's object is
 * not changed, with every other key kept as it was (the Next router's `__NA`
 * and `__PRIVATE_NEXTJS_INTERNALS_TREE`). A null or undefined state becomes
 * the stamp alone; any other value that is not an object is passed on
 * unstamped.
 */
export function withPosition(state: unknown, position: number): unknown {
  if (state === null || state === undefined) return { [POSITION_KEY]: position };
  if (typeof state !== 'object' || Array.isArray(state)) return state;
  return { ...(state as Record<string, unknown>), [POSITION_KEY]: position };
}

/** The window's parts the tracking uses, so a test can hand it a fake. */
export interface HistoryHost {
  history: {
    readonly state: unknown;
    pushState(data: unknown, unused: string, url?: string | URL | null): void;
    replaceState(data: unknown, unused: string, url?: string | URL | null): void;
  };
  addEventListener(type: 'popstate', listener: (event: { state: unknown }) => void): void;
}

/**
 * Track the position on `host`: wrap its `pushState` and `replaceState` so
 * every entry they write carries a stamp, listen for `popstate` to follow
 * Back and Forward (calling `onPop` after it), and stamp the current entry.
 * Returns the reader of the current position.
 *
 * The wrappers call the methods they replaced, whatever those are, so they
 * compose with the Next router's own patch (installed in an effect of the app
 * router, which runs after the shell's): Next's wraps ours, and a call from
 * the router passes through both.
 */
export function trackPosition(host: HistoryHost, onPop: () => void): () => number {
  const { history } = host;
  const push = history.pushState;
  const replace = history.replaceState;
  let current = positionOf(history.state) ?? 0;
  replace.call(history, withPosition(history.state, current), '');
  history.pushState = function pushState(data, unused, url) {
    const next = current + 1;
    push.call(history, withPosition(data, next), unused, url);
    current = next;
  };
  history.replaceState = function replaceState(data, unused, url) {
    const kept = positionOf(history.state) ?? current;
    replace.call(history, withPosition(data, kept), unused, url);
  };
  host.addEventListener('popstate', (event) => {
    const position = positionOf(event.state);
    if (position !== null) current = position;
    onPop();
  });
  return () => current;
}

let position: (() => number) | null = null;

/** Start tracking the position; first call wins (the app shell calls it on
 *  mount, before any in-app navigation). */
export function captureNavBaseline(): void {
  if (position === null && typeof window !== 'undefined') {
    position = trackPosition(window as unknown as HistoryHost, noteNavigation);
  }
}

/** True when the current entry is above the one the app booted on, so Back
 *  stays in the app. */
export function hasInAppHistory(): boolean {
  return position !== null && position() > 0;
}

/**
 * The subscription for a control drawn from `hasInAppHistory` (the dock's
 * Back on a dock home, src/components/app-shell.tsx), for
 * `useSyncExternalStore`. A push announces nothing, so the app shell calls
 * `noteNavigation` after each pathname commits (the router writes the history
 * entry in that same commit, before its effects run), and Back and Forward
 * call it from the `popstate` listener.
 */
const listeners = new Set<() => void>();

export function subscribeNavHistory(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A navigation committed: `hasInAppHistory` may answer differently now. */
export function noteNavigation(): void {
  for (const listener of listeners) listener();
}
